import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DialerSipIcon from "@mui/icons-material/DialerSip";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const emptyQueueHours = () => ({
  useCustom: false,
  horaInicial: "08:00",
  horaFinal: "18:00",
  diaInicial: "1",
  diaFinal: "5",
  horaMsg: "",
  horaAtivo: "enabled",
  outOfHoursQueueId: "",
});

const DAYS = [
  { value: "0", label: "Domingo" },
  { value: "1", label: "Segunda" },
  { value: "2", label: "Terça" },
  { value: "3", label: "Quarta" },
  { value: "4", label: "Quinta" },
  { value: "5", label: "Sexta" },
  { value: "6", label: "Sábado" },
];

const ENABLED_OPTIONS = [
  { value: "enabled", label: i18n.t("settings.settings.userCreation.options.enabled") },
  { value: "disabled", label: i18n.t("settings.settings.userCreation.options.disabled") },
];

const useStyles = makeStyles(theme => ({
  content: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    paddingBottom: theme.spacing(10),
  },
  section: {
    padding: theme.spacing(2.5),
    height: "100%",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  helper: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    marginTop: theme.spacing(0.5),
  },
  saveBar: {
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
}));

const SectionCard = ({ icon, title, children }) => (
  <Paper variant="outlined" sx={{ height: "100%" }}>
    <Box sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, fontWeight: 600 }}>
        {icon}
        {title}
      </Typography>
      <Stack spacing={2}>{children}</Stack>
    </Box>
  </Paper>
);

const boolToFlag = value => (value ? "1" : "0");
const flagToBool = value => value === "1" || value === 1 || value === true;

const defaultForm = {
  userCreation: "disabled",
  protocolo: true,
  historicoContato: true,
  filaCinza: true,
  pbxUrl: "",
  pbxToken: "",
  pbxPort: "5061",
  pbxTls: "enabled",
  horaInicial: "",
  horaFinal: "",
  diaInicial: "1",
  diaFinal: "5",
  horaMsg: "",
  horaAtivo: "disabled",
  horaFilaFora: "",
  queueHours: {},
  msgFim: "",
  msgFimAtivo: "disabled",
  msgPesquisa: "",
  pesquisaAtivo: "disabled",
  pesquisaInicial: "1",
  pesquisaFinal: "5",
  pesquisaTempo: "5",
  msgCall: "",
  msgCallAtivo: "disabled",
  callbackAtivo: "disabled",
  voipConnections: {},
};

const getConnectionKind = wa => {
  try {
    if (wa?.session) {
      const parsed = JSON.parse(wa.session);
      const provider = parsed?.meta?.provider;
      if (provider === "whatsapp_cloud") return "Meta Cloud (oficial)";
      if (provider === "instagram_dm") return "Instagram";
      if (provider === "website_embed") return "Chat Site";
    }
  } catch {
    // sessão wweb
  }
  return "WhatsApp Web";
};

const isVoipEligibleConnection = wa => {
  const kind = getConnectionKind(wa);
  return kind === "Meta Cloud (oficial)" || kind === "WhatsApp Web";
};

const Settings = () => {
  const classes = useStyles();
  const [form, setForm] = useState(defaultForm);
  const [queues, setQueues] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const findValue = useCallback((settings, key, fallback = "") => {
    const row = settings.find(s => s.key === key);
    return row?.value ?? fallback;
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [{ data }, queuesRes, whatsappRes] = await Promise.all([
          api.get("/settings"),
          api.get("/queue"),
          api.get("/whatsapp"),
        ]);

        let queueHours = {};
        try {
          const raw = findValue(data, "queue-hours", "{}");
          queueHours = JSON.parse(raw || "{}") || {};
        } catch {
          queueHours = {};
        }

        let voipConnections = {};
        try {
          const rawVoip = findValue(data, "voip-connections", "{}");
          voipConnections = JSON.parse(rawVoip || "{}") || {};
        } catch {
          voipConnections = {};
        }

        const queueList = queuesRes.data || [];
        setQueues(queueList);

        const whatsappList = (whatsappRes.data || []).filter(isVoipEligibleConnection);
        setConnections(whatsappList);

        const normalizedVoip = {};
        whatsappList.forEach(wa => {
          const current = voipConnections[String(wa.id)];
          normalizedVoip[String(wa.id)] =
            current === true || current === 1 || current === "1" || current === "true";
        });

        const normalizedQueueHours = {};
        queueList.forEach(q => {
          const current = queueHours[String(q.id)] || {};
          normalizedQueueHours[String(q.id)] = {
            ...emptyQueueHours(),
            ...current,
            outOfHoursQueueId:
              current.outOfHoursQueueId != null && current.outOfHoursQueueId !== ""
                ? String(current.outOfHoursQueueId)
                : "",
          };
        });

        setForm({
          userCreation: findValue(data, "userCreation", "disabled"),
          protocolo: flagToBool(findValue(data, "protocolo", "1")),
          historicoContato: flagToBool(findValue(data, "historico-contato", "1")),
          filaCinza: flagToBool(findValue(data, "msg-fila-cinza", "1")),
          pbxUrl: findValue(data, "pbx-url"),
          pbxToken: findValue(data, "pbx-token"),
          pbxPort: findValue(data, "pbx-port", "5061") || "5061",
          pbxTls: findValue(data, "pbx-tls", "enabled") || "enabled",
          horaInicial: findValue(data, "hora-inicial"),
          horaFinal: findValue(data, "hora-final"),
          diaInicial: findValue(data, "dia-inicial", "1"),
          diaFinal: findValue(data, "dia-final", "5"),
          horaMsg: findValue(data, "hora-msg"),
          horaAtivo: findValue(data, "hora-ativo", "disabled"),
          horaFilaFora: findValue(data, "hora-fila-fora", ""),
          queueHours: normalizedQueueHours,
          msgFim: findValue(data, "msg-fim"),
          msgFimAtivo: findValue(data, "msg-fim-ativo", "disabled"),
          msgPesquisa: findValue(data, "msg-pesquisa"),
          pesquisaAtivo: findValue(data, "msg-pesquisa-ativo", "disabled"),
          pesquisaInicial: findValue(data, "msg-pesquisa-inicial", "1"),
          pesquisaFinal: findValue(data, "msg-pesquisa-final", "5"),
          pesquisaTempo: findValue(data, "msg-pesquisa-tempo", "5"),
          msgCall: findValue(data, "msg-call"),
          msgCallAtivo: findValue(data, "msg-call-ativo", "disabled"),
          callbackAtivo: findValue(data, "callback-call-ativo", "disabled"),
          voipConnections: normalizedVoip,
        });
        setDirty(false);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [findValue]);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const updateQueueHours = (queueId, field, value) => {
    setForm(prev => ({
      ...prev,
      queueHours: {
        ...prev.queueHours,
        [String(queueId)]: {
          ...(prev.queueHours[String(queueId)] || emptyQueueHours()),
          [field]: value,
        },
      },
    }));
    setDirty(true);
  };

  const updateVoipConnection = (whatsappId, enabled) => {
    setForm(prev => ({
      ...prev,
      voipConnections: {
        ...prev.voipConnections,
        [String(whatsappId)]: Boolean(enabled),
      },
    }));
    setDirty(true);
  };

  const payload = useMemo(
    () => [
      { key: "userCreation", value: form.userCreation },
      { key: "protocolo", value: boolToFlag(form.protocolo) },
      { key: "historico-contato", value: boolToFlag(form.historicoContato) },
      { key: "msg-fila-cinza", value: boolToFlag(form.filaCinza) },
      {
        key: "pbx-url",
        value: String(form.pbxUrl || "")
          .trim()
          .replace(/^https?:\/\//i, "")
          .replace(/\/+$/, ""),
      },
      { key: "pbx-token", value: form.pbxToken },
      { key: "pbx-port", value: String(form.pbxPort || "5061") },
      { key: "pbx-tls", value: form.pbxTls || "enabled" },
      { key: "hora-inicial", value: form.horaInicial },
      { key: "hora-final", value: form.horaFinal },
      { key: "dia-inicial", value: form.diaInicial },
      { key: "dia-final", value: form.diaFinal },
      { key: "hora-msg", value: form.horaMsg },
      { key: "hora-ativo", value: form.horaAtivo },
      { key: "hora-fila-fora", value: form.horaFilaFora || "" },
      {
        key: "queue-hours",
        value: JSON.stringify(
          Object.fromEntries(
            Object.entries(form.queueHours || {}).map(([id, cfg]) => [
              id,
              {
                ...cfg,
                outOfHoursQueueId: cfg.outOfHoursQueueId
                  ? Number(cfg.outOfHoursQueueId)
                  : null,
              },
            ])
          )
        ),
      },
      {
        key: "voip-connections",
        value: JSON.stringify(form.voipConnections || {}),
      },
      { key: "msg-fim", value: form.msgFim },
      { key: "msg-fim-ativo", value: form.msgFimAtivo },
      { key: "msg-pesquisa", value: form.msgPesquisa },
      { key: "msg-pesquisa-ativo", value: form.pesquisaAtivo },
      { key: "msg-pesquisa-inicial", value: form.pesquisaInicial },
      { key: "msg-pesquisa-final", value: form.pesquisaFinal },
      { key: "msg-pesquisa-tempo", value: form.pesquisaTempo },
      { key: "msg-call", value: form.msgCall },
      { key: "msg-call-ativo", value: form.msgCallAtivo },
      {
        // Mantido por compatibilidade: ativo se qualquer conexão tiver VoIP ligado.
        key: "callback-call-ativo",
        value: Object.values(form.voipConnections || {}).some(Boolean)
          ? "enabled"
          : "disabled",
      },
    ],
    [form]
  );

  const validate = () => {
    const domainRegex = /^(?!-)[A-Za-z0-9-]+([\-\.]{1}[a-z0-9]+)*\.[A-Za-z]{2,6}$/;
    const host = form.pbxUrl.trim().replace(/^https?:\/\//i, "").replace(/\/+$/, "");

    if (host && !domainRegex.test(host)) {
      toast.error("URL do PBX inválida.");
      return false;
    }
    if (host && !form.pbxToken.trim()) {
      toast.error("Informe o token do PBX.");
      return false;
    }
    const port = Number(form.pbxPort || 5061);
    if (!Number.isFinite(port) || port < 1 || port > 65535) {
      toast.error("Porta do PBX inválida.");
      return false;
    }
    if (Number(form.diaFinal) < Number(form.diaInicial)) {
      toast.error("Dia inicial deve ser menor ou igual ao dia final.");
      return false;
    }
    if (
      form.pesquisaAtivo === "enabled" &&
      Number(form.pesquisaInicial) > Number(form.pesquisaFinal)
    ) {
      toast.error("Valor de pesquisa inicial deve ser menor que o final.");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await api.put("/settings/bulk", { settings: payload });
      toast.success(i18n.t("settings.success"));
      setDirty(false);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress color="primary" />
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("settings.title")}</Title>
      </MainHeader>

      <Box className={classes.content}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <SectionCard icon={<SettingsIcon color="primary" />} title="Geral">
              <TextField
                select
                fullWidth
                label={i18n.t("settings.settings.userCreation.name")}
                value={form.userCreation}
                onChange={e => updateField("userCreation", e.target.value)}
              >
                {ENABLED_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>

              <FormControlLabel
                control={<Switch checked={form.protocolo} onChange={e => updateField("protocolo", e.target.checked)} color="primary" />}
                label="Informar protocolo nos tickets"
              />
              <Typography className={classes.helper}>Protocolo é o número do ticket exibido ao cliente.</Typography>

              <FormControlLabel
                control={<Switch checked={form.historicoContato} onChange={e => updateField("historicoContato", e.target.checked)} color="primary" />}
                label="Histórico completo do contato"
              />

              <FormControlLabel
                control={<Switch checked={form.filaCinza} onChange={e => updateField("filaCinza", e.target.checked)} color="primary" />}
                label="Exibir tickets sem fila"
              />
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard
              icon={<DialerSipIcon color="primary" />}
              title="PABX IP / Chamadas WhatsApp VoIP"
            >
              <Typography className={classes.helper} sx={{ mb: 1 }}>
                Configure o PABX e ative a chamada VoIP em cada conexão WhatsApp Web
                ou Meta Cloud (token + Phone Number ID). A chamada recebida é
                encaminhada para o PABX cadastrado (URL + token) com SIP TLS na porta
                informada (padrão 5061).
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="URL / host do PBX"
                    value={form.pbxUrl}
                    onChange={e => updateField("pbxUrl", e.target.value)}
                    placeholder="voip.life-sip.com.br"
                    helperText="Somente o domínio, sem http/https"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Token do PBX"
                    value={form.pbxToken}
                    onChange={e => updateField("pbxToken", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Porta SIP"
                    value={form.pbxPort}
                    onChange={e => updateField("pbxPort", e.target.value)}
                    inputProps={{ min: 1, max: 65535 }}
                    helperText="Padrão 5061"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Conexão SIP"
                    value={form.pbxTls}
                    onChange={e => updateField("pbxTls", e.target.value)}
                  >
                    <MenuItem value="enabled">TLS (recomendado)</MenuItem>
                    <MenuItem value="disabled">TCP sem TLS</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" fontWeight={700}>
                Resposta chamadas WhatsApp
              </Typography>
              <Typography className={classes.helper} sx={{ mb: 1 }}>
                Mensagem enviada ao cliente quando a chamada no WhatsApp é recebida
                (independente do encaminhamento VoIP ao PABX).
              </Typography>
              <TextField
                select
                fullWidth
                label="Resposta chamadas WhatsApp"
                value={form.msgCallAtivo}
                onChange={e => updateField("msgCallAtivo", e.target.value)}
              >
                {ENABLED_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Texto da resposta automática"
                value={form.msgCall}
                onChange={e => updateField("msgCall", e.target.value)}
                placeholder="Este número não recebe chamadas via WhatsApp. Em breve retornaremos."
              />

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" fontWeight={700}>
                Chamada VoIP por conexão
              </Typography>
              <Typography className={classes.helper} sx={{ mb: 1 }}>
                Ative nas conexões que devem encaminhar a chamada ao PABX cadastrado
                acima (URL + token). Válido para WhatsApp Web e Meta Cloud oficial.
              </Typography>

              {connections.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma conexão WhatsApp Web ou Meta Cloud cadastrada.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {connections.map(wa => (
                    <Paper key={wa.id} variant="outlined" sx={{ px: 2, py: 1 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={600} noWrap>
                            {wa.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getConnectionKind(wa)} · ID {wa.id}
                            {wa.status ? ` · ${wa.status}` : ""}
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              color="primary"
                              checked={Boolean(form.voipConnections[String(wa.id)])}
                              onChange={e =>
                                updateVoipConnection(wa.id, e.target.checked)
                              }
                            />
                          }
                          label="Chamada VoIP"
                        />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard icon={<AccessTimeIcon color="primary" />} title="Horário de funcionamento">
              <Typography className={classes.helper} sx={{ mb: 1 }}>
                Horário global padrão. Mensagens recebidas fora desse período podem ser
                encaminhadas para uma fila específica.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth type="time" label="Início" value={form.horaInicial} onChange={e => updateField("horaInicial", e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth type="time" label="Fim" value={form.horaFinal} onChange={e => updateField("horaFinal", e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField select fullWidth label="Dia inicial" value={form.diaInicial} onChange={e => updateField("diaInicial", e.target.value)}>
                    {DAYS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField select fullWidth label="Dia final" value={form.diaFinal} onChange={e => updateField("diaFinal", e.target.value)}>
                    {DAYS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>

              <TextField select fullWidth label="Mensagem fora do horário — ativo" value={form.horaAtivo} onChange={e => updateField("horaAtivo", e.target.value)}>
                {ENABLED_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </TextField>

              <TextField fullWidth multiline minRows={3} label="Mensagem fora do horário" value={form.horaMsg} onChange={e => updateField("horaMsg", e.target.value)} placeholder="Atendemos de 08h às 18h. Retornaremos em breve!" />

              <TextField
                select
                fullWidth
                label="Fila para tickets fora do horário (global)"
                value={form.horaFilaFora || ""}
                onChange={e => updateField("horaFilaFora", e.target.value)}
                helperText="Usada quando a fila do ticket não define uma fila específica de fora de horário."
              >
                <MenuItem value="">Não redirecionar</MenuItem>
                {queues.map(q => (
                  <MenuItem key={q.id} value={String(q.id)}>
                    {q.name}
                  </MenuItem>
                ))}
              </TextField>

              <Divider />
              <Typography variant="subtitle2" fontWeight={700}>
                Horário por fila
              </Typography>
              <Typography className={classes.helper}>
                Ative horário próprio em cada fila e escolha para qual fila o ticket deve
                ir quando a mensagem chegar fora do expediente.
              </Typography>

              {queues.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma fila cadastrada.
                </Typography>
              )}

              <Stack spacing={2}>
                {queues.map(queue => {
                  const cfg = form.queueHours[String(queue.id)] || emptyQueueHours();
                  return (
                    <Paper key={queue.id} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: queue.color || "#999" }} />
                            <Typography fontWeight={600}>{queue.name}</Typography>
                          </Stack>
                          <FormControlLabel
                            control={
                              <Switch
                                color="primary"
                                checked={Boolean(cfg.useCustom)}
                                onChange={e =>
                                  updateQueueHours(queue.id, "useCustom", e.target.checked)
                                }
                              />
                            }
                            label="Horário próprio"
                          />
                        </Stack>

                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Fila destino fora do horário"
                          value={cfg.outOfHoursQueueId || ""}
                          onChange={e =>
                            updateQueueHours(queue.id, "outOfHoursQueueId", e.target.value)
                          }
                        >
                          <MenuItem value="">Usar fila global / não redirecionar</MenuItem>
                          {queues
                            .filter(q => q.id !== queue.id)
                            .map(q => (
                              <MenuItem key={q.id} value={String(q.id)}>
                                {q.name}
                              </MenuItem>
                            ))}
                        </TextField>

                        {cfg.useCustom && (
                          <>
                            <Grid container spacing={1.5}>
                              <Grid item xs={6} md={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="time"
                                  label="Início"
                                  value={cfg.horaInicial || ""}
                                  onChange={e =>
                                    updateQueueHours(queue.id, "horaInicial", e.target.value)
                                  }
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="time"
                                  label="Fim"
                                  value={cfg.horaFinal || ""}
                                  onChange={e =>
                                    updateQueueHours(queue.id, "horaFinal", e.target.value)
                                  }
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label="Dia inicial"
                                  value={cfg.diaInicial || "1"}
                                  onChange={e =>
                                    updateQueueHours(queue.id, "diaInicial", e.target.value)
                                  }
                                >
                                  {DAYS.map(d => (
                                    <MenuItem key={d.value} value={d.value}>
                                      {d.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label="Dia final"
                                  value={cfg.diaFinal || "5"}
                                  onChange={e =>
                                    updateQueueHours(queue.id, "diaFinal", e.target.value)
                                  }
                                >
                                  {DAYS.map(d => (
                                    <MenuItem key={d.value} value={d.value}>
                                      {d.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                            </Grid>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Mensagem fora do horário — ativo"
                              value={cfg.horaAtivo || "enabled"}
                              onChange={e =>
                                updateQueueHours(queue.id, "horaAtivo", e.target.value)
                              }
                            >
                              {ENABLED_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              minRows={2}
                              label="Mensagem fora do horário (fila)"
                              value={cfg.horaMsg || ""}
                              onChange={e =>
                                updateQueueHours(queue.id, "horaMsg", e.target.value)
                              }
                              placeholder="Usa a mensagem global se ficar vazio"
                            />
                          </>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard icon={<SettingsIcon color="primary" />} title="Finalização de tickets">
              <TextField select fullWidth label="Mensagem de encerramento — ativo" value={form.msgFimAtivo} onChange={e => updateField("msgFimAtivo", e.target.value)}>
                {ENABLED_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </TextField>
              <TextField fullWidth multiline minRows={3} label="Mensagem de encerramento" value={form.msgFim} onChange={e => updateField("msgFim", e.target.value)} placeholder="Este ticket será fechado. Até mais!" />
              <Typography className={classes.helper}>Use %usuario% para incluir o nome do atendente.</Typography>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard icon={<SentimentSatisfiedAltIcon color="primary" />} title="Pesquisa de satisfação">
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField select fullWidth label="Nota inicial" value={form.pesquisaInicial} onChange={e => updateField("pesquisaInicial", e.target.value)}>
                    {[0,1,2,3,4,5].map(v => <MenuItem key={v} value={String(v)}>{v}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField select fullWidth label="Nota final" value={form.pesquisaFinal} onChange={e => updateField("pesquisaFinal", e.target.value)}>
                    {[0,1,2,3,4,5,6,7,8,9,10].map(v => <MenuItem key={v} value={String(v)}>{v}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField select fullWidth label="Espera (min)" value={form.pesquisaTempo} onChange={e => updateField("pesquisaTempo", e.target.value)}>
                    {[2,5,10,15,20,30,40,60,120].map(v => <MenuItem key={v} value={String(v)}>{v}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
              <TextField select fullWidth label="Pesquisa — ativo" value={form.pesquisaAtivo} onChange={e => updateField("pesquisaAtivo", e.target.value)}>
                {ENABLED_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </TextField>
              <TextField fullWidth multiline minRows={3} label="Mensagem da pesquisa" value={form.msgPesquisa} onChange={e => updateField("msgPesquisa", e.target.value)} />
            </SectionCard>
          </Grid>

        </Grid>
      </Box>

      <Paper elevation={0} className={classes.saveBar}>
        {dirty && (
          <Typography variant="body2" color="textSecondary" sx={{ mr: "auto", alignSelf: "center" }}>
            Alterações não salvas
          </Typography>
        )}
        <Button variant="contained" color="primary" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} disabled={saving} onClick={handleSave}>
          Salvar configurações
        </Button>
      </Paper>
    </MainContainer>
  );
};

export default Settings;
